<template>
    <div>
        <illustration-heading
            :title="isMaintenance ? $tc('common.maintenance.maintenance-mode', 1) : $t('common.maintenance.temporarily-unavailable')"
            img="maintenance"
        />

        <p v-if="isMaintenance" class="q-mb-none">{{ $t('common.maintenance.maintenance-mode--info') }}</p>
        <p v-else class="q-mb-none">{{ $t('common.maintenance.temporarily-unavailable--info') }}</p>

        <p>{{ $tc(`common.maintenance.options--info${isLoggedIn ? '--logout' : ''}`, 1) }}</p>

        <div class="row justify-center q-gutter-sm q-mt-md q-mb-lg">
            <base-button
                :label="$tc('common.maintenance.check-availability', 1)"
                :loading="isChecking"
                :disable="isManualCheckDisabled"
                primary-button
                outline
                @click="manualCheck"
            />

            <base-button
                v-if="isLoggedIn"
                :label="$t('common.auth.logout-action')"
                flat
                @click="triggerLogout"
            />
        </div>

        <div class="q-mt-xl">
            <p class="additional-info text-xs text-left q-mb-none">{{ $t('common.maintenance.availability-checked-again-in') }} <span style="white-space: nowrap">{{ formattedCountdown }}</span></p>

            <q-linear-progress
                :value="countdownProgress"
                size="xs"
            />
        </div>
    </div>
</template>

<script>
import { onLogout } from '@/apollo'
import { apolloClient } from '@/apollo/client'
import { useRouter } from '@/composables/router'
import { checkApiAvailability } from '@/helpers/serviceUnavailable'
import { Cookies } from 'quasar'
import { computed, ref, onBeforeUnmount, onMounted } from 'vue'
import { formatDuration, intervalToDuration } from 'date-fns'
import { getCurrentDateFnsLocale } from '@/helpers/date'
import store from '@/store/store'

const CHECK_INTERVAL_SECONDS = 5 * 60
const MANUAL_CHECK_COOLDOWN_SECONDS = 10

export default {
    name: 'ServiceUnavailableContent',
    props: {
        isMaintenance: {
            type: Boolean,
            default: false,
        },
    },
    setup (props, { emit }) {
        const router = useRouter()
        const secondsUntilNextCheck = ref(CHECK_INTERVAL_SECONDS)
        const checkTimeout = ref(null)
        const countdownInterval = ref(null)
        const isChecking = ref(false)
        const isManualCheckOnCooldown = ref(false)
        const manualCheckCooldownTimeout = ref(null)

        const isLoggedIn = computed(() => {
            return Cookies.has('jwt_header_payload')
        })

        const isManualCheckDisabled = computed(() => {
            return isChecking.value || isManualCheckOnCooldown.value
        })

        const countdownProgress = computed(() => {
            return secondsUntilNextCheck.value / CHECK_INTERVAL_SECONDS
        })

        const formattedCountdown = computed(() => {
            return formatDuration(
                intervalToDuration({
                    start: 0,
                    end: secondsUntilNextCheck.value * 1000,
                }),
                {
                    locale: getCurrentDateFnsLocale(),
                }
            )
        })

        function getJwtPayload () {
            if (!Cookies.has('jwt_header_payload')) return null

            return JSON.parse(atob(Cookies.get('jwt_header_payload').split('.')[1]))
        }

        function isSessionExpired () {
            const payload = getJwtPayload()

            if (!payload || !payload.exp) return true

            return payload.exp * 1000 <= Date.now()
        }

        async function logoutIfSessionExpired () {
            if (!isLoggedIn.value) return
            if (!isSessionExpired()) return

            await logout('auto-logout')
        }

        function startCountdown () {
            stopAutomaticChecks()
            secondsUntilNextCheck.value = CHECK_INTERVAL_SECONDS

            countdownInterval.value = window.setInterval(() => {
                logoutIfSessionExpired()
                secondsUntilNextCheck.value = Math.max(secondsUntilNextCheck.value - 1, 0)
            }, 1000)

            checkTimeout.value = window.setTimeout(() => {
                check()
            }, CHECK_INTERVAL_SECONDS * 1000)
        }

        function stopAutomaticChecks () {
            if (checkTimeout.value) {
                window.clearTimeout(checkTimeout.value)
                checkTimeout.value = null
            }

            if (countdownInterval.value) {
                window.clearInterval(countdownInterval.value)
                countdownInterval.value = null
            }
        }

        function startManualCheckCooldown () {
            stopManualCheckCooldown()
            isManualCheckOnCooldown.value = true

            manualCheckCooldownTimeout.value = window.setTimeout(() => {
                isManualCheckOnCooldown.value = false
                manualCheckCooldownTimeout.value = null
            }, MANUAL_CHECK_COOLDOWN_SECONDS * 1000)
        }

        function stopManualCheckCooldown () {
            if (manualCheckCooldownTimeout.value) {
                window.clearTimeout(manualCheckCooldownTimeout.value)
                manualCheckCooldownTimeout.value = null
            }

            isManualCheckOnCooldown.value = false
        }

        async function check () {
            if (isChecking.value) return

            await logoutIfSessionExpired()

            stopAutomaticChecks()
            isChecking.value = true

            try {
                const availability = await checkApiAvailability()

                if (availability.isAvailable) {
                    emit('available')
                    return
                }

                startCountdown()
            } catch (error) {
                startCountdown()
            } finally {
                isChecking.value = false
            }
        }

        async function manualCheck () {
            if (isManualCheckDisabled.value) return

            try {
                await check()
            } finally {
                startManualCheckCooldown()
            }
        }

        async function triggerLogout () {
            await logout()
        }

        async function logout (reason = 'logged-out') {
            await onLogout(apolloClient)
            window.location.assign(router.resolve({ name: 'auth-logout', params: { reason } }).href)
        }

        onMounted(() => {
            store.dispatch('activateServiceUnavailable')
            logoutIfSessionExpired()
            check()
        })

        onBeforeUnmount(() => {
            store.dispatch('deactivateServiceUnavailable')
            stopAutomaticChecks()
            stopManualCheckCooldown()
        })

        return {
            secondsUntilNextCheck,
            countdownProgress,
            formattedCountdown,
            isChecking,
            isLoggedIn,
            isManualCheckDisabled,
            manualCheck,
            triggerLogout,
        }
    },
}
</script>
